import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('🔐 Admin Login Attempt:', {
      email: formData.email,
      password: formData.password ? '***' + formData.password.slice(-3) : 'MISSING',
      passwordLength: formData.password?.length,
      apiUrl: API,
      fullEndpoint: `${API}/admin/login`,
      timestamp: new Date().toISOString()
    });

    try {
      console.log('📡 Sending POST request with body:', {
        email: formData.email,
        passwordProvided: !!formData.password,
        passwordLength: formData.password?.length
      });
      
      const response = await axios.post(`${API}/admin/login`, {
        email: formData.email,
        password: formData.password
      });
      
      console.log('✅ Admin Login Success:', {
        hasToken: !!response.data.token,
        tokenLength: response.data.token?.length,
        userType: response.data.user?.user_type,
        userEmail: response.data.user?.email
      });
      
      localStorage.setItem('mojo_admin_token', response.data.token);
      localStorage.setItem('mojo_admin_user', JSON.stringify(response.data.user));
      
      toast.success('Admin login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('❌ Admin Login Failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        detail: error.response?.data?.detail,
        data: error.response?.data,
        message: error.message,
        apiUrl: `${API}/admin/login`,
        requestSent: {
          email: formData.email,
          passwordLength: formData.password?.length
        }
      });
      
      toast.error(error.response?.data?.detail || error.message || 'Login failed - Check console for details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B0C34] via-[#2D1854] to-[#1B0C34] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#F2B10C] rounded-full flex items-center justify-center">
            <Shield size={32} className="text-[#1B0C34]" />
          </div>
          <CardTitle className="text-2xl text-[#1B0C34]">Mojo Money Admin</CardTitle>
          <CardDescription>Analytics Dashboard Login</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="admin_email">Email</Label>
              <Input
                id="admin_email"
                type="email"
                placeholder="admin@mojomoney.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin_password">Password</Label>
              <Input
                id="admin_password"
                type="password"
                placeholder="Enter admin password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login as Admin'}
            </Button>
            
            <Button
              type="button"
              onClick={() => {
                console.log('🧪 TEST LOGIN: Bypassing authentication');
                // Create a test admin token
                const testToken = 'test-admin-token-' + Date.now();
                const testUser = {
                  email: 'test-admin@mojomoney.com',
                  user_type: 'admin'
                };
                
                localStorage.setItem('mojo_admin_token', testToken);
                localStorage.setItem('mojo_admin_user', JSON.stringify(testUser));
                
                console.log('✅ TEST LOGIN: Token set, navigating to dashboard');
                toast.success('Test login successful (NO SECURITY)');
                navigate('/admin/dashboard');
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
            >
              🧪 TEST LOGIN (Bypass Auth)
            </Button>
          </form>
          
          <div className="mt-4 p-3 bg-[#F2B10C]/10 rounded text-xs text-[#1B0C34]/70">
            <p className="font-semibold mb-1">New Admin Credentials:</p>
            <p>Email: admin@mojomoney.com</p>
            <p>Password: MojoAdmin2025!</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
